import React from 'react';
import FormBreadcrumb from '../FormBreadcrumb';
import UpgradeDealerInformation, {
  UpgradeDealerInformationValues,
  upgradeDealerInformationSchema,
} from './UpgradeDealerInformation';
import UpgradeDealerProducts, {
  UpgradeDealerProductValues,
  upgradeDealerProductSchema,
} from './UpgradeDealerProducts';
import { UpgradeDealerPage, UpgradeDealerFormInputsByPage, UpgradeDealerFormInputs } from './types';
import DealerInfoCheck from '../DealerInfoCheck';
import { DealerContext, IDealerContext, useDealerContextDefaultValue } from '../DealerContext';
import * as dealerService from '../../../services/dealerService';
import FormikWizardButtons from '../FormikWizardButtons';
import useDealerTestData from '../useDealerTestData';
import { FinalDealerInput } from '../../../services/dealerService/hooks';
import { DealerContacts, getDealerContacts } from '../../../redux/modules/dealer';

const renderActiveFormPage = ({
  activePage,
  pageValues,
  combinedValues,
  onPageSubmit,
  onBack,
}: {
  onBack: onPageChange;
} & Pick<FormData, 'activePage' | 'pageValues' | 'onPageSubmit' | 'combinedValues'>) => {
  switch (activePage) {
    case UpgradeDealerPage.DEALER_INFORMATION:
    default:
      return (
        <UpgradeDealerInformation
          initialValues={pageValues && pageValues[UpgradeDealerPage.DEALER_INFORMATION]}
          onSubmit={values =>
            onPageSubmit({
              pageName: UpgradeDealerPage.DEALER_INFORMATION,
              values,
              nextPage: UpgradeDealerPage.DEALER_PRODUCTS,
            })
          }
        >
          {({ errors, isValid }) => <FormikWizardButtons disableNext={!isValid} errors={errors} />}
        </UpgradeDealerInformation>
      );
    case UpgradeDealerPage.DEALER_PRODUCTS:
      return (
        <UpgradeDealerProducts
          initialValues={pageValues && pageValues[UpgradeDealerPage.DEALER_PRODUCTS]}
          onSubmit={values =>
            onPageSubmit({
              pageName: UpgradeDealerPage.DEALER_PRODUCTS,
              values,
              nextPage: UpgradeDealerPage.DEALER_CHECK,
            })
          }
        >
          {({ values, isValid, errors }) => (
            <FormikWizardButtons
              errors={errors}
              disableNext={!isValid}
              onBack={() =>
                onBack({
                  from: UpgradeDealerPage.DEALER_PRODUCTS,
                  to: UpgradeDealerPage.DEALER_INFORMATION,
                  values,
                })
              }
            />
          )}
        </UpgradeDealerProducts>
      );
    case UpgradeDealerPage.DEALER_CHECK:
      const combinedSchema = upgradeDealerInformationSchema.concat(upgradeDealerProductSchema);

      return (
        <DealerInfoCheck<UpgradeDealerFormInputs, typeof combinedSchema>
          dealerFormInputs={combinedValues}
          combinedSchema={combinedSchema}
          displayName="Upgrade Dealer form"
        >
          {({ isValid }) => (
            <FormikWizardButtons
              disableNext={!isValid}
              nextButtonText="Submit"
              onBack={() => onBack({ to: UpgradeDealerPage.DEALER_PRODUCTS })}
              onNext={() =>
                onPageSubmit({
                  pageName: UpgradeDealerPage.DEALER_CHECK,
                  values: null,
                  nextPage: null,
                })
              }
            />
          )}
        </DealerInfoCheck>
      );
  }
};

interface UpgradeDealerProps {
  dealerDetails: dealerService.DealerDetails;
  initialFormValues?: UpgradeDealerFormInputsByPage;
  initialActivePage?: UpgradeDealerPage;
  onSubmit: (formInputs: FinalDealerInput) => Promise<void>;
}

interface UpgradeDealerFormDataProps extends UpgradeDealerProps {
  savedContacts: DealerContacts | null;
}

type PageSubmit = (
  info:
    | {
        pageName: UpgradeDealerPage.DEALER_INFORMATION;
        values: UpgradeDealerInformationValues;
        nextPage: UpgradeDealerPage;
      }
    | {
        pageName: UpgradeDealerPage.DEALER_PRODUCTS;
        values: UpgradeDealerProductValues;
        nextPage: UpgradeDealerPage;
      }
    | {
        pageName: UpgradeDealerPage.DEALER_CHECK;
        values: null;
        nextPage: null;
      },
) => Promise<void>;

type onPageChange = (backArgs: {
  from?: UpgradeDealerPage;
  to: UpgradeDealerPage;
  values?: UpgradeDealerFormInputs;
}) => void;

interface FormData {
  dealerContext: IDealerContext;
  pageValues: UpgradeDealerFormInputsByPage;
  combinedValues: UpgradeDealerInformationValues & UpgradeDealerProductValues;
  activePage: UpgradeDealerPage;
  onPageSubmit: PageSubmit;
  onActivePageChange: onPageChange;
}

/**
 * Pre-fill signer/owner fields, preferring the dealer's last portal submission and falling back
 * to 4D's owner name and dealer email. Empty values stay undefined so required validation applies.
 */
const getDealerContactDefaults = (
  dealerDetails: dealerService.DealerDetails,
  savedContacts: DealerContacts | null,
) => {
  const { firstName, lastName } = dealerService.getOwnerNameFromDetails(dealerDetails.dlOwner);
  return {
    documentSignerFirstName: savedContacts?.documentSignerFirstName || firstName || undefined,
    documentSignerLastName: savedContacts?.documentSignerLastName || lastName || undefined,
    documentSignersPositionTitle: savedContacts?.documentSignersPositionTitle || undefined,
    ownerEmail: savedContacts?.ownerEmail || dealerDetails.dlEmail || undefined,
  };
};

const useFormData = ({
  dealerDetails,
  initialFormValues,
  initialActivePage,
  onSubmit,
  savedContacts,
}: UpgradeDealerFormDataProps): FormData => {
  const dealerContext = useDealerContextDefaultValue({ dealerDetails });

  const hasSpecialRateProducts = dealerService.hooks.useUpgradesHaveSpecialRatesProducts(
    dealerContext.upgradeEligibleProducts,
  );
  const { getUpgradeDealerTestData } = useDealerTestData();

  const [pageValues, setPageValues] = React.useState<UpgradeDealerFormInputsByPage>(
    initialFormValues ??
      getUpgradeDealerTestData({ dealerContactEmail: dealerDetails.dlEmail }) ?? {
        [UpgradeDealerPage.DEALER_INFORMATION]: {
          ...getDealerContactDefaults(dealerDetails, savedContacts),
          dealerContactEmail: dealerDetails.dlEmail,
          appointmentDate: new Date(),
          hasPENIntegration: false,
        },
        [UpgradeDealerPage.DEALER_PRODUCTS]: {
          appointedProducts: [],
          supplementalProducts: [],
          isGapPlus: false,
          isBHPH: false,
          retailPack: false,
          // asn: DealerSoftware.NO_SOFTWARE,
          retailPackProducts: [],
          rateType: hasSpecialRateProducts ? 'SPECIAL' : 'STANDARD',
          includeNCBAddendum: false,
          ncbAddendumProducts: [],
          productionIncentive: false,
          productionIncentiveProducts: [],
          dealerW9Forms: [],
        },
      },
  );
  const [activePage, setActivePage] = React.useState<UpgradeDealerPage>(
    initialActivePage ?? UpgradeDealerPage.DEALER_INFORMATION,
  );

  const dealerDetailInformation = dealerService.hooks.useDealerDetailsAsFormInputs(
    dealerDetails,
    pageValues[UpgradeDealerPage.DEALER_PRODUCTS].appointedProducts,
  );

  const combinedValues = Object.values(pageValues).reduce(
    (combined, values) => ({ ...combined, ...values }),
    dealerDetailInformation,
  );

  const dealer = dealerService.hooks.useTrimUnusedDealerFields(combinedValues);
  const finalDealer = dealerService.hooks.useConvertedValues(combinedValues);

  const onPageSubmit: PageSubmit = async ({ pageName, values, nextPage }) => {
    setPageValues({ ...pageValues, [pageName]: values });
    if (nextPage) {
      setActivePage(nextPage);
    }

    if (nextPage == null && pageValues) {
      // if no next page, then its a final submittal
      // combine the form inputs with values we derived from the dealerDetails
      await onSubmit(finalDealer);
    }
  };

  const handleActivePageChange: onPageChange = ({ from, to, values }) => {
    // here we are storing partial inputs to the form - to better UX
    if (values != null && from != null) {
      setPageValues({ ...pageValues, [from]: values });
    }
    setActivePage(to);
  };

  return {
    dealerContext,
    pageValues,
    combinedValues: dealer,
    activePage,
    onPageSubmit,
    onActivePageChange: handleActivePageChange,
  };
};

/**
 * Load saved dealer contacts before mounting the form, since Formik only reads initialValues once
 */
const useSavedDealerContacts = (prcoDealerId: number) => {
  const [state, setState] = React.useState<{ loading: boolean; contacts: DealerContacts | null }>({
    loading: true,
    contacts: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    getDealerContacts(prcoDealerId)
      .catch(() => null) // pre-fill is best effort, fall back to 4D values
      .then(contacts => {
        if (!cancelled) setState({ loading: false, contacts });
      });
    return () => {
      cancelled = true;
    };
  }, [prcoDealerId]);

  return state;
};

export default function UpgradeDealerForm(props: UpgradeDealerProps) {
  const { loading, contacts } = useSavedDealerContacts(props.dealerDetails.dlid);

  if (loading) {
    return <div>Loading dealer information...</div>;
  }

  return <UpgradeDealerFormContent {...props} savedContacts={contacts} />;
}

function UpgradeDealerFormContent(props: UpgradeDealerFormDataProps) {
  const {
    dealerContext,
    pageValues,
    combinedValues,
    activePage,
    onActivePageChange,
    onPageSubmit,
  } = useFormData(props);

  return (
    <div>
      <DealerContext.Provider value={dealerContext}>
        <FormBreadcrumb
          items={Object.values(UpgradeDealerPage)}
          current={activePage}
          onClick={nextPage => onActivePageChange({ to: nextPage })}
          disableLastItem={process.env.NODE_ENV !== 'development'}
        />
        {renderActiveFormPage({
          pageValues,
          combinedValues,
          activePage,
          onPageSubmit,
          onBack: onActivePageChange,
        })}
      </DealerContext.Provider>
    </div>
  );
}
